import { useEffect, useState } from 'react'
import { useReservations } from '@/hooks/useReservations'
import { useAuth } from '@/hooks/useAuth'
import {Calendar, Clock, User, Phone, Mail, Car, MapPin, Sparkles, CreditCard, FileText, Shield, ChevronLeft, AlertTriangle, CheckCircle2, CalendarCheck} from 'lucide-react'
import { useNavigate } from 'react-router-dom'

export default function MyReservationsPage() {
  const { reservations, loading, fetchReservations, updateReservation } = useReservations()
  const { user, isAuthenticated } = useAuth()
  const navigate = useNavigate()
  const [filter, setFilter] = useState('all')
  const [processingPickupId, setProcessingPickupId] = useState<string | null>(null)

  // Une reservation est "en retard de prise en charge" si :
  // - la date de debut est passee
  // - le statut est encore pending/confirmed/paid (le vehicule n'a pas ete pris)
  const isPickupOverdue = (reservation: any): boolean => {
    if (!reservation?.startDate) return false
    const pendingStatuses = ['pending', 'confirmed', 'paid']
    if (!pendingStatuses.includes(reservation.status)) return false
    return new Date(reservation.startDate).getTime() < Date.now()
  }

  const handleConfirmPickup = async (reservation: any) => {
    if (!reservation?._id) return
    const confirmed = window.confirm(
      `Confirmer la prise en charge du vehicule "${reservation.vehicleName}" pour la reservation #${reservation.reservationNumber} ?\n\nLe statut passera a "En cours".`
    )
    if (!confirmed) return
    setProcessingPickupId(reservation._id)
    try {
      await updateReservation(reservation._id, {
        status: 'active',
        updatedAt: new Date().toISOString(),
      })
    } catch (err) {
      console.error('Update pickup failed:', err)
      window.alert("Impossible de confirmer la prise en charge. Veuillez reessayer.")
    } finally {
      setProcessingPickupId(null)
    }
  }

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/')
      return
    }
    fetchReservations({
      sort: { createdAt: -1 }
    })
  }, [isAuthenticated])

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      pending: 'bg-gradient-to-r from-yellow-400 to-yellow-500 text-black',
      confirmed: 'bg-gradient-to-r from-blue-400 to-blue-600 text-white',
      paid: 'bg-gradient-to-r from-green-400 to-green-600 text-white',
      active: 'bg-gradient-to-r from-purple-400 to-purple-600 text-white',
      completed: 'bg-gradient-to-r from-gray-400 to-gray-600 text-white',
      cancelled: 'bg-gradient-to-r from-red-400 to-red-600 text-white',
    }
    return colors[status] || 'bg-gray-500 text-white'
  }

  const getStatusLabel = (status: string) => {
    const labels: Record<string, string> = {
      pending: 'En attente',
      confirmed: 'Confirmée',
      paid: 'Payée',
      active: 'En cours',
      completed: 'Terminée',
      cancelled: 'Annulée',
    }
    return labels[status] || status
  }

  const filteredReservations = filter === 'all' 
    ? reservations 
    : reservations.filter((r) => r.status === filter)

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block w-16 h-16 border-4 border-yellow-400 border-t-transparent rounded-full animate-spin mb-4" />
          <div className="text-2xl text-white font-medium">Chargement...</div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900">
      {/* Header */}
      <div className="bg-black text-white py-12 border-b border-yellow-400/20 shadow-2xl">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-yellow-400 hover:text-yellow-300 mb-4 transition-colors"
          >
            <ChevronLeft className="w-5 h-5" />
            Retour à l'accueil
          </button>
          <div className="flex items-center gap-3">
            <FileText className="w-10 h-10 text-yellow-400" />
            <div>
              <h1 className="text-4xl font-black bg-gradient-to-r from-yellow-400 via-yellow-300 to-yellow-400 bg-clip-text text-transparent">
                Mes réservations
              </h1>
              <p className="text-gray-400 mt-1">Bienvenue, {user?.userName}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Filters */}
        <div className="bg-gradient-to-r from-gray-800 to-gray-900 rounded-2xl shadow-xl p-6 mb-8 border-2 border-gray-700">
          <div className="flex items-center gap-2 mb-4">
            <Sparkles className="w-5 h-5 text-yellow-400" />
            <h3 className="text-lg font-bold text-white">Filtrer par statut</h3>
          </div>
          <div className="flex gap-2 overflow-x-auto pb-2">
            {['all', 'pending', 'confirmed', 'paid', 'active', 'completed'].map((status) => (
              <button
                key={status}
                onClick={() => setFilter(status)}
                className={`px-5 py-2.5 rounded-xl font-bold whitespace-nowrap transition-all duration-300 ${
                  filter === status
                    ? 'bg-gradient-to-r from-yellow-400 to-yellow-500 text-black shadow-lg shadow-yellow-400/30'
                    : 'bg-gray-700 text-gray-300 hover:bg-gray-600 border border-gray-600'
                }`}
              >
                {status === 'all' ? 'Toutes' : getStatusLabel(status)}
              </button>
            ))}
          </div>
        </div>

        {/* Reservations List */}
        {filteredReservations.length === 0 ? (
          <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl shadow-2xl p-16 text-center border-2 border-gray-700">
            <Car className="w-20 h-20 text-yellow-400 mx-auto mb-6 opacity-50" />
            <h3 className="text-2xl font-bold text-white mb-3">
              Aucune réservation
            </h3>
            <p className="text-gray-400 mb-8 text-lg">
              Vous n'avez pas encore de réservation dans cette catégorie.
            </p>
            <button
              onClick={() => navigate('/flotte')}
              className="inline-flex items-center gap-2 bg-gradient-to-r from-yellow-400 to-yellow-500 text-black px-8 py-4 rounded-xl font-bold hover:shadow-2xl hover:shadow-yellow-400/50 transition-all hover:scale-105"
            >
              <Sparkles className="w-5 h-5" />
              Découvrir nos véhicules
            </button>
          </div>
        ) : (
          <div className="space-y-6">
            {filteredReservations.map((reservation) => (
              <div
                key={reservation._id}
                className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl shadow-2xl overflow-hidden border-2 border-gray-700 hover:border-yellow-400/50 transition-all duration-300 hover:shadow-yellow-400/10"
              >
                {/* Header */}
                <div className="bg-black/50 p-6 border-b border-gray-700">
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="text-2xl font-black text-white mb-2 flex items-center gap-2">
                        <Car className="w-6 h-6 text-yellow-400" />
                        {reservation.vehicleName}
                      </h3>
                      <p className="text-sm text-gray-400 font-mono">
                        Réservation #{reservation.reservationNumber}
                      </p>
                      {reservation.createdAt && (
                        <p className="text-xs text-gray-500 mt-1 flex items-center gap-1.5">
                          <CalendarCheck className="w-3.5 h-3.5 text-yellow-400/70" />
                          Réservée le {new Date(reservation.createdAt).toLocaleDateString('fr-CH', { day: '2-digit', month: 'long', year: 'numeric' })} à {new Date(reservation.createdAt).toLocaleTimeString('fr-CH', { hour: '2-digit', minute: '2-digit' })}
                        </p>
                      )}
                    </div>
                    <span className={`px-4 py-2 rounded-xl text-sm font-bold shadow-lg ${getStatusColor(reservation.status)}`}>
                      {getStatusLabel(reservation.status)}
                    </span>
                  </div>
                </div>

                {/* Alerte prise en charge non effectuee */}
                {isPickupOverdue(reservation) && (
                  <div className="bg-gradient-to-r from-red-900/50 to-orange-900/50 border-b-2 border-red-500/60 p-5">
                    <div className="flex items-start gap-4">
                      <div className="flex-shrink-0 w-11 h-11 rounded-full bg-red-500/20 border-2 border-red-400 flex items-center justify-center animate-pulse">
                        <AlertTriangle className="w-6 h-6 text-red-300" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="font-bold text-red-200 text-lg mb-1">
                          Prise en charge non effectuée
                        </h4>
                        <p className="text-red-100/90 text-sm mb-3">
                          La date de début (<strong>{new Date(reservation.startDate).toLocaleString('fr-CH')}</strong>) est dépassée et le véhicule n'a pas encore été pris en charge.
                        </p>
                        <button
                          onClick={() => handleConfirmPickup(reservation)}
                          disabled={processingPickupId === reservation._id}
                          className="inline-flex items-center gap-2 bg-gradient-to-r from-green-500 to-emerald-600 text-white px-5 py-2.5 rounded-xl font-bold hover:shadow-xl hover:shadow-green-500/40 transition-all hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                        >
                          <CheckCircle2 className="w-5 h-5" />
                          {processingPickupId === reservation._id ? 'Traitement...' : 'Confirmer la prise en charge'}
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Content Grid */}
                <div className="p-6">
                  <div className="grid md:grid-cols-2 gap-6 mb-6">
                    {/* Dates Section */}
                    <div className="bg-gradient-to-br from-blue-900/30 to-blue-800/20 rounded-xl p-5 border border-blue-700/30">
                      <div className="flex items-center gap-2 mb-4">
                        <Calendar className="w-5 h-5 text-blue-400" />
                        <h4 className="font-bold text-white">Période de location</h4>
                      </div>
                      <div className="space-y-3">
                        <div className="flex items-start gap-3">
                          <Clock className="w-4 h-4 text-blue-300 mt-1 flex-shrink-0" />
                          <div>
                            <p className="text-xs text-gray-400 mb-1">Début</p>
                            <p className="text-white font-semibold">
                              {new Date(reservation.startDate).toLocaleString('fr-CH')}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-start gap-3">
                          <Clock className="w-4 h-4 text-blue-300 mt-1 flex-shrink-0" />
                          <div>
                            <p className="text-xs text-gray-400 mb-1">Fin</p>
                            <p className="text-white font-semibold">
                              {new Date(reservation.endDate).toLocaleString('fr-CH')}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-start gap-3">
                          <MapPin className="w-4 h-4 text-yellow-400 mt-1 flex-shrink-0" />
                          <div>
                            <p className="text-xs text-gray-400 mb-1">Lieu</p>
                            <p className="text-white font-semibold">{reservation.pickupLocation}</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Client Info Section */}
                    <div className="bg-gradient-to-br from-purple-900/30 to-purple-800/20 rounded-xl p-5 border border-purple-700/30">
                      <div className="flex items-center gap-2 mb-4">
                        <User className="w-5 h-5 text-purple-400" />
                        <h4 className="font-bold text-white">Informations client</h4>
                      </div>
                      <div className="space-y-3">
                        <div className="flex items-center gap-3">
                          <User className="w-4 h-4 text-purple-300 flex-shrink-0" />
                          <span className="text-white font-semibold">{reservation.clientName}</span>
                        </div>
                        <div className="flex items-center gap-3">
                          <Phone className="w-4 h-4 text-purple-300 flex-shrink-0" />
                          <span className="text-gray-300">{reservation.clientPhone}</span>
                        </div>
                        <div className="flex items-center gap-3">
                          <Mail className="w-4 h-4 text-purple-300 flex-shrink-0" />
                          <span className="text-gray-300 break-all">{reservation.clientEmail}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Options */}
                  {reservation.options && reservation.options.length > 0 && (
                    <div className="bg-gradient-to-br from-green-900/30 to-green-800/20 rounded-xl p-5 mb-6 border border-green-700/30">
                      <div className="flex items-center gap-2 mb-3">
                        <Sparkles className="w-5 h-5 text-green-400" />
                        <h4 className="font-bold text-white">Options sélectionnées</h4>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {reservation.options.map((opt: any, idx: number) => (
                          <span
                            key={idx}
                            className="px-3 py-1.5 bg-green-700/50 text-green-200 text-sm rounded-lg font-medium border border-green-600/50"
                          >
                            {opt.name} — CHF {opt.price}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Price Summary */}
                  <div className="bg-gradient-to-br from-black to-gray-900 rounded-xl p-6 border-2 border-yellow-400 shadow-lg">
                    <div className="flex justify-between items-center mb-4">
                      <div className="flex items-center gap-2">
                        <CreditCard className="w-6 h-6 text-yellow-400" />
                        <h4 className="font-bold text-white text-lg">Récapitulatif financier</h4>
                      </div>
                    </div>
                    <div className="space-y-3">
                      <div className="flex justify-between items-center pb-3 border-b border-gray-700">
                        <span className="text-gray-300">Prix total:</span>
                        <span className="text-3xl font-black bg-gradient-to-r from-yellow-400 to-yellow-500 bg-clip-text text-transparent">
                          CHF {reservation.totalPrice}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <div className="flex items-center gap-2">
                          <Shield className="w-4 h-4 text-yellow-400" />
                          <span className="text-gray-300">Caution:</span>
                        </div>
                        <span className="text-xl font-bold text-white">
                          CHF {reservation.deposit}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
